// Frontend mirror of supabase/functions/_shared/plan-config.ts
//
// Why a mirror instead of a direct import:
//   - Vite cannot import from supabase/functions reliably across the Deno
//     boundary in all build environments.
//   - Both files are tiny pure data. A drift test (src/test/planConfig.test.ts)
//     asserts deep-equality at CI time, so they cannot diverge in practice.
//
// Rules:
//   - Numbers ONLY exist here on the frontend side. No component, hook, page,
//     or copy file may hardcode a quota, price, or tier label.
//   - To change a number: edit BOTH this file AND
//     supabase/functions/_shared/plan-config.ts. The test will fail otherwise.

export type Tier = "guest" | "free" | "edge" | "operator";
export type QuotaWindow = "day" | "month";

export interface PlanQuota {
  max: number;
  window: QuotaWindow;
}

export interface PlanIntent {
  label: string;
  roleTag: string;
  priceMonthly: number | null;
  priceYearly: number | null;
  monthlyPriceId: string | null;
  yearlyPriceId: string | null;
  upgradeTo: Tier | null;
}

export const PLAN_CONFIG: Record<Tier, { quota: PlanQuota; intent: PlanIntent }> = {
  guest: {
    quota: { max: 3, window: "day" },
    intent: {
      label: "Guest",
      roleTag: "Preview",
      priceMonthly: null,
      priceYearly: null,
      monthlyPriceId: null,
      yearlyPriceId: null,
      upgradeTo: "free",
    },
  },
  free: {
    quota: { max: 3, window: "day" },
    intent: {
      label: "Free",
      roleTag: "The call",
      priceMonthly: 0,
      priceYearly: 0,
      monthlyPriceId: null,
      yearlyPriceId: null,
      upgradeTo: "edge",
    },
  },
  edge: {
    quota: { max: 400, window: "month" },
    intent: {
      label: "Edge",
      roleTag: "The move",
      priceMonthly: 19,
      priceYearly: 190,
      monthlyPriceId: "edge_monthly_v1",
      yearlyPriceId: "edge_yearly_v1",
      upgradeTo: "operator",
    },
  },
  operator: {
    quota: { max: 1000, window: "month" },
    intent: {
      label: "Operator",
      roleTag: "The system",
      priceMonthly: 49,
      priceYearly: 490,
      monthlyPriceId: "operator_monthly_v1",
      yearlyPriceId: "operator_yearly_v1",
      upgradeTo: null,
    },
  },
};

export function getQuota(tier: Tier): PlanQuota {
  return PLAN_CONFIG[tier].quota;
}

export function getIntent(tier: Tier): PlanIntent {
  return PLAN_CONFIG[tier].intent;
}

/** Defensive coercion. Unknown / legacy → "free" (never silently grant paid). */
export function normalizeTier(input: string | null | undefined): Tier {
  if (input === "guest" || input === "free" || input === "edge" || input === "operator") {
    return input;
  }
  if (input === "elite") return "operator";
  if (input === "pro" || input === "starter") return "edge";
  return "free";
}

export const ALL_TIERS: Tier[] = ["guest", "free", "edge", "operator"];
